/**
 * Pagina Tickets (FKA "Triage Vulnerabilità") (E10.4 / RF-5-1/5-2 / E10.5).
 *
 * Route: /tickets e /tickets/:projectUuid (le storiche /triage… fanno redirect)
 *
 * Funzionalità:
 * - Tabella finding (CVE, severità, componente, stato analisi/VEX, KEV se disponibile)
 * - Form inline per aggiornare stato + justification + note + commento (gated VULNERABILITY_ANALYSIS)
 * - Pulsante Export VEX: scarica il documento CycloneDX VEX come JSON (E10.5)
 * - Gestione stati loading / empty / error (mostra ApiError.message)
 */

import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams, useLocation, useNavigate, Link } from 'react-router-dom';
import { usePermissions } from '../../auth/index.js';
import { ApiError } from '../../api/client.js';
import { useTriage, useUpdateAnalysis, useVexExport } from './hooks.js';
import { TicketList } from './TicketList.js';
import { useBundles, useProducts } from '../products/hooks.js';
import { useGlobalCves, useCveDetail } from '../cve/hooks.js';
import type { GlobalCveEntry, AffectedRelease } from '../cve/types.js';
import type { AnalysisState, AnalysisJustification, TriageItem } from './types.js';
import { TICKETS_PATH, currentTabHash, ticketsReleasePath, ticketsTabFromHash } from './routes.js';
import styles from './Triage.module.css';
import { KevDashboardPage } from '../kev/KevDashboardPage.js';

// ---------------------------------------------------------------------------
// Costanti CycloneDX
// ---------------------------------------------------------------------------

const STATI_ANALISI: AnalysisState[] = [
  'NOT_SET',
  'IN_TRIAGE',
  'EXPLOITABLE',
  'FALSE_POSITIVE',
  'NOT_AFFECTED',
  'RESOLVED',
];

const GIUSTIFICAZIONI: AnalysisJustification[] = [
  'CODE_NOT_PRESENT',
  'CODE_NOT_REACHABLE',
  'REQUIRES_CONFIGURATION',
  'REQUIRES_DEPENDENCY',
  'REQUIRES_ENVIRONMENT',
  'PROTECTED_BY_COMPILER',
  'PROTECTED_AT_RUNTIME',
  'PROTECTED_AT_PERIMETER',
  'PROTECTED_BY_MITIGATING_CONTROL',
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Tronca un testo a `max` caratteri aggiungendo un'ellissi (testo completo nel title). */
function troncaTesto(testo: string, max: number): string {
  return testo.length > max ? `${testo.slice(0, max).trimEnd()}…` : testo;
}

/** Restituisce la classe CSS del badge in base alla severità. */
function classeSeverita(severity: string | undefined): string {
  const sev = (severity ?? '').toUpperCase();
  if (sev === 'CRITICAL') return styles.badgeCritical ?? '';
  if (sev === 'HIGH') return styles.badgeHigh ?? '';
  if (sev === 'MEDIUM') return styles.badgeMedium ?? '';
  if (sev === 'LOW') return styles.badgeLow ?? '';
  return styles.badgeInfo ?? '';
}

/** Restituisce la classe CSS del badge in base allo stato analisi. */
function classeStato(stato: string): string {
  const b = styles.badge ?? '';
  if (stato === 'RESOLVED') return `${b} ${styles.badgeStateResolved ?? ''}`;
  if (stato === 'FALSE_POSITIVE') return `${b} ${styles.badgeStateFalsePositive ?? ''}`;
  if (stato === 'NOT_AFFECTED') return `${b} ${styles.badgeStateNotAffected ?? ''}`;
  if (stato === 'EXPLOITABLE') return `${b} ${styles.badgeStateExploitable ?? ''}`;
  return `${b} ${styles.badgeState ?? ''}`;
}

// ---------------------------------------------------------------------------
// Componente form inline di aggiornamento analisi
// ---------------------------------------------------------------------------

interface FormAnalisiProps {
  item: TriageItem;
  projectUuid: string;
  onChiudi: () => void;
}

function FormAnalisi({ item, projectUuid, onChiudi }: FormAnalisiProps) {
  const [stato, setStato] = useState<AnalysisState>(item.analysisState);
  const [giustificazione, setGiustificazione] = useState<string>(item.justification ?? '');
  const [note, setNote] = useState('');
  const [commento, setCommento] = useState('');

  const { mutate, isPending, isError, error, isSuccess } = useUpdateAnalysis(projectUuid);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    mutate(
      {
        componentUuid: item.componentUuid,
        vulnerabilityUuid: item.vulnerabilityUuid,
        state: stato,
        justification: giustificazione || undefined,
        details: note || undefined,
        comment: commento || undefined,
      },
      {
        onSuccess: () => {
          onChiudi();
        },
      },
    );
  }

  return (
    <form
      className={styles.formCard}
      onSubmit={handleSubmit}
      data-testid={`form-analisi-${item.vulnerabilityUuid}`}
    >
      <p className={styles.formTitle}>Aggiorna analisi — {item.cveId}</p>

      <div className={styles.formRow}>
        {/* Stato analisi */}
        <div className={styles.formGroup}>
          <label className={styles.formGroupLabel} htmlFor={`stato-${item.vulnerabilityUuid}`}>
            Stato analisi
          </label>
          <select
            id={`stato-${item.vulnerabilityUuid}`}
            className={styles.formSelect}
            value={stato}
            onChange={(e) => {
              setStato(e.target.value as AnalysisState);
            }}
            data-testid={`select-stato-${item.vulnerabilityUuid}`}
          >
            {STATI_ANALISI.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        {/* Giustificazione */}
        <div className={styles.formGroup}>
          <label
            className={styles.formGroupLabel}
            htmlFor={`giustificazione-${item.vulnerabilityUuid}`}
          >
            Giustificazione
          </label>
          <select
            id={`giustificazione-${item.vulnerabilityUuid}`}
            className={styles.formSelect}
            value={giustificazione}
            onChange={(e) => {
              setGiustificazione(e.target.value);
            }}
            data-testid={`select-giustificazione-${item.vulnerabilityUuid}`}
          >
            <option value="">— nessuna —</option>
            {GIUSTIFICAZIONI.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className={styles.formRow}>
        {/* Dettagli/note */}
        <div className={styles.formGroup}>
          <label className={styles.formGroupLabel} htmlFor={`note-${item.vulnerabilityUuid}`}>
            Note (details)
          </label>
          <textarea
            id={`note-${item.vulnerabilityUuid}`}
            className={styles.formTextarea}
            value={note}
            onChange={(e) => {
              setNote(e.target.value);
            }}
            placeholder="Dettagli analisi..."
            data-testid={`textarea-note-${item.vulnerabilityUuid}`}
          />
        </div>

        {/* Commento */}
        <div className={styles.formGroup}>
          <label className={styles.formGroupLabel} htmlFor={`commento-${item.vulnerabilityUuid}`}>
            Commento
          </label>
          <textarea
            id={`commento-${item.vulnerabilityUuid}`}
            className={styles.formTextarea}
            value={commento}
            onChange={(e) => {
              setCommento(e.target.value);
            }}
            placeholder="Commento audit..."
            data-testid={`textarea-commento-${item.vulnerabilityUuid}`}
          />
        </div>
      </div>

      <div className={styles.formActions}>
        <button
          type="submit"
          className={styles.btnPrimary}
          disabled={isPending}
          data-testid={`btn-salva-analisi-${item.vulnerabilityUuid}`}
        >
          {isPending ? 'Salvataggio...' : 'Salva analisi'}
        </button>
        <button
          type="button"
          className={styles.btnSecondary}
          onClick={onChiudi}
          data-testid={`btn-annulla-analisi-${item.vulnerabilityUuid}`}
        >
          Annulla
        </button>
        {isError && (
          <span
            className={styles.inlineMutError}
            data-testid={`errore-analisi-${item.vulnerabilityUuid}`}
          >
            {error instanceof ApiError ? error.message : (error?.message ?? 'Errore sconosciuto')}
          </span>
        )}
        {isSuccess && (
          <span
            className={styles.successMsg}
            data-testid={`successo-analisi-${item.vulnerabilityUuid}`}
          >
            Analisi aggiornata.
          </span>
        )}
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Componente riga triage
// ---------------------------------------------------------------------------

interface RigaTriageProps {
  item: TriageItem;
  projectUuid: string;
  puoScrivere: boolean;
}

function RigaTriage({ item, projectUuid, puoScrivere }: RigaTriageProps) {
  const [formAperto, setFormAperto] = useState(false);

  return (
    <>
      <tr data-testid={`riga-triage-${item.vulnerabilityUuid}`}>
        <td style={{ whiteSpace: 'nowrap' }}>
          <span style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{item.cveId}</span>
        </td>
        <td>
          {item.severity ? (
            <span className={`${styles.badge} ${classeSeverita(item.severity)}`}>
              {item.severity}
            </span>
          ) : (
            <span className={`${styles.badge} ${styles.badgeInfo}`}>N/D</span>
          )}
        </td>
        <td>
          <span style={{ fontWeight: 500 }}>{item.componentName}</span>
          {item.componentVersion && (
            <span style={{ color: '#6c757d', marginLeft: '0.4rem', fontSize: '0.82rem' }}>
              {item.componentVersion}
            </span>
          )}
        </td>
        <td>
          <span className={classeStato(item.analysisState)}>{item.analysisState}</span>
          {item.isSuppressed && (
            <span
              className={`${styles.badge} ${styles.badgeInfo}`}
              style={{ marginLeft: '0.4rem' }}
            >
              soppresso
            </span>
          )}
        </td>
        <td>
          {item.justification ? (
            <span style={{ fontSize: '0.82rem', color: '#6c757d' }}>{item.justification}</span>
          ) : (
            <span style={{ color: '#adb5bd' }}>—</span>
          )}
        </td>
        <td data-testid={`descrizione-triage-${item.vulnerabilityUuid}`}>
          {item.description ? (
            <span className={styles.descCell} tabIndex={0}>
              {troncaTesto(item.description, 160)}
              <span className={styles.descTooltip} role="tooltip">
                {item.description}
              </span>
            </span>
          ) : (
            <span style={{ color: '#adb5bd' }}>—</span>
          )}
        </td>
        <td>
          {puoScrivere && (
            <button
              type="button"
              className={`${styles.btnSecondary} ${styles.btnSmall}`}
              onClick={() => {
                setFormAperto((v) => !v);
              }}
              data-testid={`btn-modifica-${item.vulnerabilityUuid}`}
            >
              {formAperto ? 'Chiudi' : 'Modifica'}
            </button>
          )}
        </td>
      </tr>
      {formAperto && (
        <tr>
          <td colSpan={7} style={{ padding: '0.5rem 1rem' }}>
            <FormAnalisi
              item={item}
              projectUuid={projectUuid}
              onChiudi={() => {
                setFormAperto(false);
              }}
            />
          </td>
        </tr>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Pannello release affette (espanso sotto una riga CVE della lista globale)
// ---------------------------------------------------------------------------

/**
 * Riga della tabella interna "release affette": un bundle (foglia) impattato dalla CVE.
 * `releaseName`/`bundleName` sono i nomi risolti dal linkage dei bundle attivi; se assenti si
 * ricade sull'UUID corrispondente.
 */
function RigaReleaseAffetta({
  release,
  releaseName,
  bundleName,
}: {
  release: AffectedRelease;
  releaseName?: string;
  bundleName?: string;
}) {
  // Target del triage = foglia (bundle) scansionata: i finding vivono lì.
  const leaf = release.bundleProjectUuid;
  const rowKey = leaf ?? release.releaseProjectUuid;
  return (
    <tr data-testid={`riga-release-${rowKey}`}>
      <td>
        {release.productInternalId ? (
          <Link
            to={`/products/${release.productInternalId}`}
            data-testid={`link-prodotto-${rowKey}`}
          >
            {release.productInternalId}
          </Link>
        ) : (
          <span style={{ color: '#adb5bd' }}>—</span>
        )}
      </td>
      <td>
        <span style={{ fontWeight: 500 }}>{releaseName ?? release.releaseProjectUuid}</span>
      </td>
      <td>
        {leaf ? (
          <span style={{ fontWeight: 500 }}>{bundleName ?? leaf}</span>
        ) : (
          <span style={{ color: '#adb5bd' }}>—</span>
        )}
      </td>
      <td>
        {release.componentName ? (
          <>
            <span style={{ fontWeight: 500 }}>{release.componentName}</span>
            {release.componentVersion && (
              <span style={{ color: '#6c757d', marginLeft: '0.4rem', fontSize: '0.82rem' }}>
                {release.componentVersion}
              </span>
            )}
          </>
        ) : (
          <span style={{ color: '#adb5bd' }}>—</span>
        )}
      </td>
      <td>
        {release.severity ? (
          <span className={`${styles.badge} ${classeSeverita(release.severity)}`}>
            {release.severity}
          </span>
        ) : (
          <span className={`${styles.badge} ${styles.badgeInfo}`}>N/D</span>
        )}
      </td>
      <td>
        <span
          className={classeStato(release.analysisState ?? 'NOT_SET')}
          data-testid={`stato-vex-${rowKey}`}
        >
          {release.analysisState ?? 'NOT_SET'}
        </span>
      </td>
      <td>
        {leaf ? (
          <Link
            to={ticketsReleasePath(leaf)}
            className={`${styles.btnSecondary} ${styles.btnSmall}`}
            style={{ whiteSpace: 'nowrap' }}
            data-testid={`link-triage-${rowKey}`}
          >
            Apri triage
          </Link>
        ) : (
          <span style={{ color: '#adb5bd' }}>—</span>
        )}
      </td>
    </tr>
  );
}

/**
 * Pannello (lazy) con le release/bundle affette da una CVE, mostrato quando la riga è espansa.
 * Carica il dettaglio CVE solo quando montato (riga aperta) tramite useCveDetail.
 */
function PannelloReleaseAffette({ cveId }: { cveId: string }) {
  const { data, isLoading, isError, error } = useCveDetail(cveId);
  const { data: bundles } = useBundles();

  // Mappe UUID → nome dal linkage dei bundle attivi (release name = version, bundle name = label).
  const releaseNameByUuid = new Map<string, string>();
  const bundleNameByUuid = new Map<string, string>();
  for (const b of bundles ?? []) {
    if (b.releaseProjectUuid && !releaseNameByUuid.has(b.releaseProjectUuid)) {
      releaseNameByUuid.set(b.releaseProjectUuid, b.version);
    }
    if (b.bundleProjectUuid && b.componentName) {
      bundleNameByUuid.set(b.bundleProjectUuid, b.componentName);
    }
  }

  if (isLoading) {
    return (
      <p className={styles.loadingMsg} data-testid={`release-affette-loading-${cveId}`}>
        Caricamento release affette…
      </p>
    );
  }
  if (isError) {
    return (
      <div className={styles.errorMsg} data-testid={`release-affette-error-${cveId}`}>
        {error instanceof ApiError ? error.message : (error?.message ?? 'Errore sconosciuto')}
      </div>
    );
  }
  if (!data || data.affected.length === 0) {
    return (
      <div className={styles.emptyState} data-testid={`release-affette-vuote-${cveId}`}>
        Nessuna release affetta trovata.
      </div>
    );
  }

  return (
    <div className={styles.expandedPanel}>
      <p className={styles.panelTitle}>Release affette con stato VEX</p>
      <table className={styles.table} data-testid={`tabella-release-affette-${cveId}`}>
        <thead>
          <tr>
            <th>Prodotto</th>
            <th>Release</th>
            <th>Bundle</th>
            <th>Componente</th>
            <th>Severità</th>
            <th>Stato VEX</th>
            <th>Azioni</th>
          </tr>
        </thead>
        <tbody>
          {data.affected.map((r, i) => (
            <RigaReleaseAffetta
              key={`${r.releaseProjectUuid}-${r.bundleProjectUuid ?? i}`}
              release={r}
              releaseName={releaseNameByUuid.get(r.releaseProjectUuid)}
              bundleName={
                r.bundleProjectUuid ? bundleNameByUuid.get(r.bundleProjectUuid) : undefined
              }
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Lista globale aggregata (vista /tickets senza projectUuid)
// ---------------------------------------------------------------------------

/** Numero di colonne della tabella globale (per il colSpan della riga espansa). */
const COLONNE_GLOBALE = 6;

/**
 * Riga della lista globale per una CVE. L'intera riga è cliccabile e funge da disclosure:
 * il click (o Invio/Spazio) espande/comprime un pannello con le release affette. Il deep-link al
 * triage per-bundle vive nel pannello espanso.
 */
function RigaCveGlobale({
  voce,
  apertaIniziale,
}: {
  voce: GlobalCveEntry;
  apertaIniziale: boolean;
}) {
  const [aperta, setAperta] = useState(apertaIniziale);
  const rigaRef = useRef<HTMLTableRowElement | null>(null);

  // Auto-scroll alla riga pre-espansa (deep-link /tickets?cve=...).
  useEffect(() => {
    if (apertaIniziale && rigaRef.current) {
      rigaRef.current.scrollIntoView({ block: 'center' });
    }
  }, [apertaIniziale]);

  function toggle() {
    setAperta((v) => !v);
  }

  return (
    <>
      <tr
        ref={rigaRef}
        className={styles.cveRowClickable}
        role="button"
        tabIndex={0}
        aria-expanded={aperta}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
        }}
        data-testid={`riga-triage-globale-${voce.cveId}`}
      >
        <td style={{ whiteSpace: 'nowrap' }}>
          <span className={styles.caret} aria-hidden="true">
            {aperta ? '▾' : '▸'}
          </span>
          <span style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{voce.cveId}</span>
        </td>
        <td>
          <span className={`${styles.badge} ${classeSeverita(voce.severity)}`}>
            {voce.severity ?? 'N/D'}
          </span>
        </td>
        <td>
          {voce.knownExploited ? (
            <span className={`${styles.badge} ${styles.badgeStateExploitable ?? ''}`}>KEV</span>
          ) : (
            <span style={{ color: '#adb5bd' }}>—</span>
          )}
        </td>
        <td>{voce.affectedReleasesCount}</td>
        <td data-testid={`descrizione-cve-${voce.cveId}`}>
          {voce.description ? (
            <span className={styles.descCell} tabIndex={0}>
              {troncaTesto(voce.description, 160)}
              <span className={styles.descTooltip} role="tooltip">
                {voce.description}
              </span>
            </span>
          ) : (
            <span style={{ color: '#adb5bd' }}>—</span>
          )}
        </td>
        <td style={{ textAlign: 'right', color: '#6c757d', fontSize: '0.8rem' }}>
          {aperta ? 'Comprimi' : 'Espandi'}
        </td>
      </tr>
      {aperta && (
        <tr data-testid={`riga-espansa-${voce.cveId}`}>
          <td colSpan={COLONNE_GLOBALE} style={{ padding: 0 }}>
            <PannelloReleaseAffette cveId={voce.cveId} />
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Lista aggregata di TUTTE le vulnerabilità del parco release attive (gerarchia Slice 6/7).
 * Mostrata quando non è selezionata una foglia specifica: ogni riga è espandibile e mostra inline
 * le release/bundle affette, con il deep-link al triage per-bundle.
 *
 * `cveDaEspandere` (da /tickets?cve=...) pre-espande e scrolla alla riga corrispondente.
 */
function ListaGlobaleVuln({ cveDaEspandere }: { cveDaEspandere?: string }) {
  // Ordinamento di default (US-2.7.2/US-2.8.1: knownExploited → no-VEX → cvssScore desc) applicato
  // server-side (GET /cve): nessun resort client-side, l'ordine del backend è autoritativo.
  const { data: voci, isLoading, isError, error } = useGlobalCves();

  if (isLoading) {
    return (
      <p className={styles.loadingMsg} data-testid="triage-global-loading">
        Caricamento vulnerabilità…
      </p>
    );
  }
  if (isError) {
    return (
      <div className={styles.errorMsg} data-testid="triage-global-error">
        {error instanceof ApiError ? error.message : (error?.message ?? 'Errore sconosciuto')}
      </div>
    );
  }
  if (!voci || voci.length === 0) {
    return (
      <div className={styles.emptyState} data-testid="triage-global-vuoto">
        Nessuna vulnerabilità rilevata sulle release attive. Verificare che siano stati catturati
        snapshot.
      </div>
    );
  }

  return (
    <table className={styles.table} data-testid="tabella-triage-globale">
      <thead>
        <tr>
          <th>CVE ID</th>
          <th>Severità</th>
          <th>KEV</th>
          <th>Release impattate</th>
          <th>Descrizione</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {voci.map((voce: GlobalCveEntry) => (
          <RigaCveGlobale
            key={voce.cveId}
            voce={voce}
            apertaIniziale={voce.cveId === cveDaEspandere}
          />
        ))}
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------------------
// Breadcrumb gerarchia (prodotto › release › bundle) della foglia in triage
// ---------------------------------------------------------------------------

/**
 * Breadcrumb NON cliccabile che mostra la gerarchia della foglia in triage:
 * nome-prodotto › nome-release › nome-bundle. I nomi sono risolti dal linkage dei bundle attivi
 * (GET /bundles) e dalla lista prodotti (GET /products). Se la foglia non è ancora risolvibile
 * (dati in caricamento o linkage assente) ricade sull'identificativo grezzo della foglia.
 */
function BreadcrumbFoglia({ projectUuid }: { projectUuid: string }) {
  const { data: bundles } = useBundles();
  const { data: products } = useProducts();

  const bundle = bundles?.find((b) => b.bundleProjectUuid === projectUuid);
  const productName =
    products?.find((p) => p.internalId === bundle?.productInternalId)?.name ??
    bundle?.productInternalId;

  const segmenti = bundle
    ? [productName, bundle.version, bundle.componentName ?? '(bundle senza nome)'].filter(
        (s): s is string => Boolean(s),
      )
    : [];

  if (segmenti.length === 0) {
    return (
      <span className={styles.breadcrumb} data-testid="triage-breadcrumb">
        <span className={styles.breadcrumbItem}>{projectUuid}</span>
      </span>
    );
  }

  return (
    <nav
      className={styles.breadcrumb}
      aria-label="Gerarchia bundle"
      data-testid="triage-breadcrumb"
    >
      {segmenti.map((seg, i) => (
        <span key={`${i}-${seg}`}>
          {i > 0 && (
            <span className={styles.breadcrumbSep} aria-hidden="true">
              ›
            </span>
          )}
          <span className={styles.breadcrumbItem}>{seg}</span>
        </span>
      ))}
    </nav>
  );
}

// ---------------------------------------------------------------------------
// Componente principale TriagePage
// ---------------------------------------------------------------------------

interface TriagePageProps {
  /** projectUuid iniettato direttamente (usato nei test; in produzione si usa useParams). */
  projectUuid?: string;
}

export function TriagePage({ projectUuid: projectUuidProp }: TriagePageProps = {}) {
  const { projectUuid: paramUuid } = useParams<{ projectUuid: string }>();
  // Sola lettura: la foglia si seleziona tramite deep-link dalla lista globale, non da una barra
  // di ricerca. projectUuidProp è iniettato nei test.
  const projectUuid = projectUuidProp ?? paramUuid ?? '';

  // Deep-link /tickets?cve=<id>: pre-espande e scrolla alla CVE nella lista globale.
  const [searchParams] = useSearchParams();
  const cveDaEspandere = searchParams.get('cve') ?? undefined;

  // Tab della vista index (driven dall'hash): #by-product (default) | #per-cve | #per-kev.
  const location = useLocation();
  const navigate = useNavigate();
  const tabAttiva = ticketsTabFromHash(location.hash);
  // Slug storici (#all-cves, #kev): la scheda si apre comunque, e la URL viene riallineata allo
  // slug corrente (replace: niente voce in più nella history).
  const hashCorrente = currentTabHash(location.hash);
  useEffect(() => {
    if (hashCorrente) {
      navigate(`${location.pathname}${location.search}${hashCorrente}`, { replace: true });
    }
  }, [hashCorrente, location.pathname, location.search, navigate]);

  const { can } = usePermissions();
  const puoScrivere = can('VULNERABILITY_ANALYSIS');

  // La lista arriva già ordinata dal server (US-2.7.2/US-2.8.1); `Page<TriageItem>.items`.
  const { data: pagina, isLoading, isError, error } = useTriage(projectUuid);
  const items = pagina?.items;
  const { mutate: esportaVex, isPending: esportaInCorso } = useVexExport(projectUuid);

  /** Avvia il download del documento VEX come file JSON. */
  function handleEsportaVex() {
    esportaVex(undefined, {
      onSuccess: (vex) => {
        const blob = new Blob([JSON.stringify(vex, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `vex-${projectUuid}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      },
    });
  }

  return (
    <div data-testid="triage-page">
      {/* Intestazione */}
      <div className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Tickets</h1>
        <span className={styles.pageSubtitle}>E10.4 / RF-5</span>
      </div>

      {/* Toolbar: breadcrumb gerarchia + Export VEX */}
      {projectUuid && (
        <div className={styles.toolbar}>
          <BreadcrumbFoglia projectUuid={projectUuid} />
          <div className={styles.toolbarRight}>
            <button
              type="button"
              className={styles.btnExport}
              onClick={handleEsportaVex}
              disabled={esportaInCorso || !projectUuid}
              data-testid="btn-export-vex"
            >
              {esportaInCorso ? 'Export in corso...' : '⬇ Export VEX'}
            </button>
          </div>
        </div>
      )}

      {/* Vista index (nessuna foglia selezionata): tab Per prodotto / Per CVE / Per KEV */}
      {!projectUuid && (
        <section data-testid="triage-global">
          <nav className={styles.tabs} role="tablist" aria-label="Viste triage">
            <Link
              to={`${TICKETS_PATH}#by-product`}
              role="tab"
              aria-selected={tabAttiva === 'by-product'}
              className={`${styles.tab} ${tabAttiva === 'by-product' ? styles.tabActive : ''}`}
              data-testid="tab-by-product"
            >
              Per prodotto
            </Link>
            <Link
              to={`${TICKETS_PATH}#per-cve`}
              role="tab"
              aria-selected={tabAttiva === 'per-cve'}
              className={`${styles.tab} ${tabAttiva === 'per-cve' ? styles.tabActive : ''}`}
              data-testid="tab-per-cve"
            >
              Per CVE
            </Link>
            <Link
              to={`${TICKETS_PATH}#per-kev`}
              role="tab"
              aria-selected={tabAttiva === 'per-kev'}
              className={`${styles.tab} ${tabAttiva === 'per-kev' ? styles.tabActive : ''}`}
              data-testid="tab-per-kev"
            >
              Per KEV
            </Link>
          </nav>

          {tabAttiva === 'per-cve' && (
            <div role="tabpanel" data-testid="tabpanel-per-cve">
              <p style={{ fontSize: '0.85rem', color: '#6c757d', margin: '0.5rem 0 1rem' }}>
                Tutte le vulnerabilità delle release attive. Clicca una riga per espandere le
                release affette e aprire il triage per-bundle.
              </p>
              <ListaGlobaleVuln cveDaEspandere={cveDaEspandere} />
            </div>
          )}

          {tabAttiva === 'per-kev' && (
            <div role="tabpanel" data-testid="tabpanel-per-kev">
              <KevDashboardPage />
            </div>
          )}

          {tabAttiva === 'by-product' && (
            <div role="tabpanel" data-testid="tabpanel-by-product">
              <p style={{ fontSize: '0.85rem', color: '#6c757d', margin: '0.5rem 0 1rem' }}>
                Ticket attivi: release con CVE da valutare (automatici) o segnalazioni create a mano
                con &quot;Nuovo ticket&quot;. Clicca una riga per i dettagli e per prendere in
                carico l&apos;indagine.
              </p>
              <TicketList />
            </div>
          )}
        </section>
      )}

      {/* Stati caricamento / errore (vista per-foglia) */}
      {isLoading && (
        <p className={styles.loadingMsg} data-testid="triage-loading">
          Caricamento finding...
        </p>
      )}

      {isError && (
        <div className={styles.errorMsg} data-testid="triage-error">
          {error instanceof ApiError ? error.message : (error?.message ?? 'Errore sconosciuto')}
        </div>
      )}

      {/* Tabella triage */}
      {!isLoading && !isError && items && items.length === 0 && (
        <div className={styles.emptyState} data-testid="triage-vuoto">
          Nessun finding di vulnerabilità per questa release.
        </div>
      )}

      {!isLoading && !isError && items && items.length > 0 && (
        <table className={styles.table} data-testid="tabella-triage">
          <thead>
            <tr>
              <th>CVE ID</th>
              <th>Severità</th>
              <th>Componente</th>
              <th>Stato VEX</th>
              <th>Giustificazione</th>
              <th>Descrizione</th>
              <th>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <RigaTriage
                key={`${item.componentUuid}-${item.vulnerabilityUuid}`}
                item={item}
                projectUuid={projectUuid}
                puoScrivere={puoScrivere}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
